// =============================================================================
// RH › Pesquisa de Escala — acompanhamento (campanha + placar do envio)
// =============================================================================
// Renderizada embutida no hub RHForms.js (que já cuida do Sidebar e do "voltar").
//
// A tela tem DOIS blocos, e a separação é a mesma do backend:
//   • PARTICIPAÇÃO — contagens e percentual. Fala de respostas, nunca de pessoas.
//   • ENVIO — nome, celular, quem falhou e por quê. Fala de pessoas, nunca de
//     respostas.
// ⚠️ Nenhum lugar da tela mostra as duas coisas juntas: ligar "fulano" a "o que
// fulano respondeu" é justamente o que o desenho do módulo impede.
//
// O placar de envio segue o padrão do PPI: quatro classes, e a distinção entre
// "Não enviado" e "Erro" existe porque o conserto é em lugares diferentes —
// cadastro × ManyChat.

import React, { useState, useEffect, useCallback } from 'react';
import {
    RefreshCw, Send, Play, Square, AlertTriangle, CheckCircle2,
    Clock, PhoneOff, XCircle, Users, Eye, X, BellRing, Percent,
} from 'lucide-react';
import PesquisaEscalaExterno from './RHFormsPesquisaEscala_Externo';

const COR = '#e11d48';
const COR_ESCURA = '#be123c';
const COR_FUNDO = '#fff1f2';

// As quatro classes do estado derivado da fila. A cor e o texto de ajuda dizem
// ONDE se conserta cada uma — é o que torna o placar acionável em vez de decorativo.
const CLASSES = {
    pendente:   { rotulo: 'Pendente',    cor: '#1e40af', fundo: '#dbeafe', icone: Clock,     ajuda: 'Na fila, aguardando o envio.' },
    naoEnviado: { rotulo: 'Não enviado', cor: '#92400e', fundo: '#fef3c7', icone: PhoneOff,  ajuda: 'Nem tentamos: o número no cadastro está inválido. Conserto: cadastro.' },
    erro:       { rotulo: 'Erro',        cor: '#991b1b', fundo: '#fee2e2', icone: XCircle,   ajuda: 'Tentamos e o WhatsApp/ManyChat recusou. Conserto: ManyChat.' },
};

const formatarData = (valor) => (valor ? new Date(valor).toLocaleDateString('pt-BR') : '—');

function PesquisaEscalaConsulta() {
    const [status, setStatus] = useState(null);
    const [envio, setEnvio] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [ocupado, setOcupado] = useState(false);
    const [aviso, setAviso] = useState(null);
    const [erro, setErro] = useState(null);
    const [selecionados, setSelecionados] = useState([]);
    const [previaAberta, setPreviaAberta] = useState(false);

    const cabecalho = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            const [rStatus, rEnvio] = await Promise.all([
                fetch('/api/rh/pesquisa-escala/status', { headers: cabecalho() }),
                fetch('/api/rh/pesquisa-escala/envio', { headers: cabecalho() }),
            ]);
            if (!rStatus.ok || !rEnvio.ok) throw new Error('Não foi possível carregar os dados da pesquisa.');
            setStatus(await rStatus.json());
            setEnvio(await rEnvio.json());
        } catch (e) {
            setErro(e.message);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    // Toda ação segue o mesmo roteiro: trava a tela, chama, mostra o retorno do
    // servidor (não uma mensagem inventada aqui) e recarrega.
    const acao = async (caminho, corpo, confirmacao) => {
        if (confirmacao && !window.confirm(confirmacao)) return;
        setOcupado(true);
        setAviso(null);
        try {
            const resposta = await fetch(`/api/rh/pesquisa-escala/${caminho}`, {
                method: 'POST',
                headers: { ...cabecalho(), 'Content-Type': 'application/json' },
                body: JSON.stringify(corpo || {}),
            });
            const dados = await resposta.json();
            if (!resposta.ok) throw new Error(dados.message || 'A operação não pôde ser concluída.');
            setAviso({ tipo: 'ok', texto: dados.message });
            setSelecionados([]);
            await carregar();
        } catch (e) {
            setAviso({ tipo: 'erro', texto: e.message });
        } finally {
            setOcupado(false);
        }
    };

    const alternarSelecao = (id) =>
        setSelecionados((atual) => (atual.includes(id) ? atual.filter((i) => i !== id) : [...atual, id]));

    if (carregando) {
        return <p style={{ color: '#64748b', padding: '20px' }}>Carregando…</p>;
    }

    // ⚠️ Falha ao carregar NÃO derruba a tela inteira — vira um aviso no topo e o
    // resto continua utilizável. Motivo concreto: enquanto a migration não roda em
    // produção, /status e /envio falham (as tabelas não existem) — e um `return`
    // aqui esconderia justamente o botão "Ver formulário", que não depende de
    // tabela nenhuma. Painel quebrado não deve levar a página junto.
    const ativa = status?.ativa;

    // Quantas pessoas o lembrete deve alcançar: recebeu o link menos respondeu.
    // ⚠️ São dois AGREGADOS de fontes diferentes (a fila e os tokens) — é uma
    // estimativa, não uma lista, e é assim de propósito: ligar as duas pontas para
    // saber exatamente quem falta é justamente o que a pesquisa não faz.
    // ⚠️ `Math.max(0, …)` porque as duas contagens são lidas em chamadas separadas
    // e podem se cruzar com uma resposta chegando no meio.
    const faltamResponder = Math.max(0, (envio?.enviados || 0) - (status?.totalRespondidos || 0));
    const cartao = {
        backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
        border: '1px solid #f0e2e2', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    };

    const Numero = ({ valor, rotulo, cor, fundo, Icone, ajuda }) => (
        <div style={{ backgroundColor: fundo, borderRadius: '12px', padding: '16px', flex: '1 1 150px', minWidth: '150px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: cor, marginBottom: '6px' }}>
                {Icone && <Icone size={16} />}
                <span style={{ fontSize: '12.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' }}>{rotulo}</span>
            </div>
            {/* tabular-nums: mantém os dígitos alinhados quando os números mudam */}
            <div style={{ fontSize: '26px', fontWeight: 700, color: cor, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{valor}</div>
            {ajuda && <div style={{ fontSize: '11.5px', color: cor, opacity: 0.85, marginTop: '6px', lineHeight: 1.4 }}>{ajuda}</div>}
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* PRÉVIA DO FORMULÁRIO — renderiza o componente PÚBLICO em modo prévia,
                não uma cópia dele: é o que garante que o RH confere exatamente a
                tela que o colaborador recebe. Envio desligado; nada é gravado. */}
            {previaAberta && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Prévia do formulário da Pesquisa de Escala"
                    onClick={(e) => { if (e.target === e.currentTarget) setPreviaAberta(false); }}
                    style={{
                        position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)',
                        zIndex: 1000, overflowY: 'auto', padding: '24px 12px',
                    }}
                >
                    <div style={{ position: 'relative', maxWidth: '720px', margin: '0 auto' }}>
                        <button
                            onClick={() => setPreviaAberta(false)}
                            aria-label="Fechar prévia"
                            style={{
                                position: 'sticky', top: 0, marginLeft: 'auto', display: 'flex',
                                alignItems: 'center', gap: '8px', padding: '10px 16px', zIndex: 2,
                                borderRadius: '8px', border: 'none', backgroundColor: '#0f172a',
                                color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                            }}
                        >
                            <X size={16} /> Fechar
                        </button>
                        <div style={{ borderRadius: '14px', overflow: 'hidden', marginTop: '-42px' }}>
                            <PesquisaEscalaExterno previa />
                        </div>
                    </div>
                </div>
            )}

            {erro && (
                <div style={{
                    backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
                    borderRadius: '12px', padding: '14px 18px', fontSize: '14px', lineHeight: 1.5,
                }}>
                    <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                    {erro} <span style={{ opacity: 0.8 }}>O botão “Ver formulário” continua funcionando.</span>
                </div>
            )}

            {aviso && (
                <div style={{
                    backgroundColor: aviso.tipo === 'ok' ? '#ecfdf5' : '#fef2f2',
                    border: `1px solid ${aviso.tipo === 'ok' ? '#a7f3d0' : '#fecaca'}`,
                    color: aviso.tipo === 'ok' ? '#065f46' : '#991b1b',
                    borderRadius: '12px', padding: '14px 18px', fontSize: '14px', lineHeight: 1.5,
                }}>
                    {aviso.texto}
                </div>
            )}

            {/* ── CAMPANHA ─────────────────────────────────────────────────── */}
            <div style={cartao}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                            Pesquisa de Escala
                        </h3>
                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b', lineHeight: 1.5 }}>
                            {status?.existe
                                ? <>Período de {formatarData(status.data_inicio)} a {formatarData(status.data_fim)} · <strong style={{ color: ativa ? '#059669' : '#64748b' }}>{ativa ? 'Em andamento' : 'Encerrada'}</strong></>
                                : 'Nenhuma pesquisa foi aberta ainda.'}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {/* Fica ANTES do "Abrir pesquisa" de propósito: a ordem dos
                            botões é a ordem em que se usa — confere o formulário,
                            depois dispara para 800 pessoas. */}
                        <button onClick={() => setPreviaAberta(true)} style={botao('secundario', false)}>
                            <Eye size={16} /> Ver formulário
                        </button>
                        <button onClick={carregar} disabled={ocupado} style={botao('neutro', ocupado)}>
                            <RefreshCw size={16} /> Atualizar
                        </button>
                        {ativa ? (
                            <button
                                onClick={() => acao('config/desativar', null,
                                    'Encerrar a pesquisa? O envio automático para. Quem já recebeu o link não conseguirá mais responder.')}
                                disabled={ocupado}
                                style={botao('perigo', ocupado)}
                            >
                                <Square size={16} /> Encerrar
                            </button>
                        ) : (
                            <button
                                onClick={() => acao('config/ativar', null,
                                    'Abrir a pesquisa e começar o envio por WhatsApp para toda a operação?')}
                                disabled={ocupado}
                                style={botao('primario', ocupado)}
                            >
                                <Play size={16} /> Abrir pesquisa
                            </button>
                        )}
                    </div>
                </div>

                {!status?.existe && (
                    <div style={{ marginTop: '18px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '14px', fontSize: '13.5px', color: '#92400e', lineHeight: 1.6 }}>
                        <strong>Antes de abrir:</strong> confira se a base de Linhares está atualizada.
                        Quem tiver saído e ainda constar como ativo vai receber o link — e isso não tem desfazer.
                    </div>
                )}
            </div>

            {/* ── PARTICIPAÇÃO (lado anônimo) ──────────────────────────────── */}
            {status?.existe && (
                <div style={cartao}>
                    <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Participação</h4>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '18px' }}>
                        <Numero valor={status.totalElegiveis} rotulo="Elegíveis" cor="#334155" fundo="#f1f5f9" Icone={Users} />
                        <Numero valor={status.totalRespondidos} rotulo="Responderam" cor="#065f46" fundo="#d1fae5" Icone={CheckCircle2} />
                        <Numero
                            valor={`${status.percentual}%`}
                            rotulo={`Meta: ${status.meta}%`}
                            cor={status.metaAtingida ? '#065f46' : '#92400e'}
                            fundo={status.metaAtingida ? '#d1fae5' : '#fef3c7'}
                        />
                        {/* ⚠️ A meta é medida sobre os ELEGÍVEIS (é assim que foi
                            combinada). Esta é a outra leitura: de quem recebeu,
                            quanto respondeu — ela mede a pesquisa, enquanto a de
                            cima mede pesquisa + qualidade do cadastro juntas. */}
                        <Numero
                            valor={`${status.taxaResposta}%`}
                            rotulo="Taxa de resposta"
                            cor="#334155"
                            fundo="#f1f5f9"
                            Icone={Percent}
                            ajuda="de quem recebeu o link"
                        />
                    </div>

                    <div style={{ backgroundColor: '#e2e8f0', borderRadius: '99px', height: '12px', overflow: 'hidden', position: 'relative' }}>
                        <div style={{
                            width: `${Math.min(status.percentual, 100)}%`, height: '100%',
                            backgroundColor: status.metaAtingida ? '#10b981' : COR,
                            borderRadius: '99px', transition: 'width 0.3s ease',
                        }} />
                        {/* Marca da meta: mostra a que distância se está sem precisar fazer conta. */}
                        <div style={{
                            position: 'absolute', top: 0, bottom: 0, left: `${Math.min(status.meta, 100)}%`,
                            width: '2px', backgroundColor: '#475569',
                        }} />
                    </div>

                    {/* ⚠️ A legenda dos DOIS percentuais. Sem ela, quem vê dois números
                        diferentes para "a mesma coisa" desconfia do sistema — e o que
                        está no meio deles é acionável: gente que o cadastro deixou fora
                        do alcance, listada no placar do envio, logo abaixo nesta tela.

                        ⚠️⚠️ DE PROPÓSITO esta frase NÃO repete o total de quem recebeu
                        o link, embora o /status o traga (`totalReceberam`): ele conta
                        quem já teve link entregue alguma vez, e o placar do envio, dois
                        blocos abaixo, mostra "Enviados" — que é o ESTADO ATUAL da fila e
                        fica 2 ou 3 menor, porque quem foi recolocado para reenvio sai de
                        "Enviados" até sair de novo. Os dois números estão certos; lado a
                        lado, sem essa explicação, pareceriam erro de contagem. */}
                    {status.totalReceberam > 0 && (
                        <p style={{ fontSize: '12.5px', color: '#64748b', margin: '12px 0 0 0', lineHeight: 1.55 }}>
                            A barra e a <strong>meta</strong> são sobre os{' '}
                            <strong>{status.totalElegiveis}</strong> elegíveis — a operação inteira,
                            inclusive quem <strong>não foi alcançado</strong> (celular ausente ou
                            inválido no cadastro, ou recusado pelo WhatsApp). A{' '}
                            <strong>taxa de resposta</strong> olha só quem recebeu o link, então ela
                            mede a pesquisa, e não o cadastro. Quem ficou de fora está no{' '}
                            <strong>placar do envio</strong>, abaixo, com nome e motivo.
                        </p>
                    )}

                    {status.porCentroCusto?.length > 0 && (
                        <div style={{ marginTop: '18px' }}>
                            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 10px 0' }}>
                                Respostas por centro de custo
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {status.porCentroCusto.map((c) => (
                                    <span key={c.NOME_CENTRO_CUSTO} style={{
                                        backgroundColor: COR_FUNDO, color: COR_ESCURA, borderRadius: '99px',
                                        padding: '5px 12px', fontSize: '12.5px', fontWeight: 600,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}>
                                        {c.NOME_CENTRO_CUSTO || '—'}: {c.RESPONDIDOS}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── ENVIO (lado identificado) ────────────────────────────────── */}
            {envio?.existe && (
                <div style={cartao}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Envio pelo WhatsApp</h4>
                        {ativa && (
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <button onClick={() => acao('envio/enfileirar')} disabled={ocupado} style={botao('neutro', ocupado)}>
                                    <Users size={16} /> Buscar novos elegíveis
                                </button>
                                <button onClick={() => acao('envio/disparar-agora')} disabled={ocupado} style={botao('secundario', ocupado)}>
                                    <Send size={16} /> Disparar agora
                                </button>

                                {/* ⚠️ A ÚNICA ação da tela que manda mensagem PAGA para quem já
                                    recebeu — por isso o número esperado vai na confirmação, e não
                                    só um "tem certeza?".
                                    ⚠️ O número é uma ESTIMATIVA por diferença de dois agregados
                                    (recebeu − respondeu); a tela não sabe, nem pode saber, QUEM
                                    são — quem descarta os respondentes é o worker, no envio. */}
                                {faltamResponder > 0 && (
                                    <button
                                        onClick={() => acao('envio/relembrar', null,
                                            `Enviar o lembrete para quem ainda não respondeu?\n\n`
                                            + `Aproximadamente ${faltamResponder} pessoa(s) vão receber a mensagem de novo `
                                            + `(${envio.enviados} receberam o link · ${status.totalRespondidos} já responderam).\n\n`
                                            + `Quem já respondeu NÃO recebe nada — o envio descarta essas pessoas sozinho.\n`
                                            + `O disparo leva cerca de ${Math.ceil((faltamResponder * 1.5) / 60)} minuto(s).`)}
                                        disabled={ocupado}
                                        style={botao('secundario', ocupado)}
                                    >
                                        <BellRing size={16} /> Reenviar para quem não respondeu ({faltamResponder})
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                        <Numero valor={envio.enviados} rotulo="Enviados" cor="#065f46" fundo="#d1fae5" Icone={CheckCircle2} />
                        <Numero valor={envio.pendentes} rotulo={CLASSES.pendente.rotulo} cor={CLASSES.pendente.cor} fundo={CLASSES.pendente.fundo} Icone={CLASSES.pendente.icone} ajuda={CLASSES.pendente.ajuda} />
                        <Numero valor={envio.naoEnviados} rotulo={CLASSES.naoEnviado.rotulo} cor={CLASSES.naoEnviado.cor} fundo={CLASSES.naoEnviado.fundo} Icone={CLASSES.naoEnviado.icone} ajuda={CLASSES.naoEnviado.ajuda} />
                        <Numero valor={envio.erros} rotulo={CLASSES.erro.rotulo} cor={CLASSES.erro.cor} fundo={CLASSES.erro.fundo} Icone={CLASSES.erro.icone} ajuda={CLASSES.erro.ajuda} />
                    </div>

                    {envio.naoRecebidos?.length > 0 ? (
                        <>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <p style={{ fontSize: '13px', color: '#475569', margin: 0 }}>
                                    <strong>{envio.naoRecebidos.length}</strong> ainda não receberam o link.
                                </p>
                                {ativa && (
                                    <button
                                        onClick={() => acao('envio/reenviar', selecionados.length ? { ids: selecionados } : null)}
                                        disabled={ocupado}
                                        style={botao('secundario', ocupado)}
                                    >
                                        <Send size={16} />
                                        {selecionados.length ? `Reenviar ${selecionados.length} marcado(s)` : 'Reenviar para todos que faltam'}
                                    </button>
                                )}
                            </div>

                            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', textAlign: 'left', color: '#475569' }}>
                                            <th style={celaCabecalho}></th>
                                            <th style={celaCabecalho}>Colaborador</th>
                                            <th style={celaCabecalho}>Centro de custo</th>
                                            <th style={celaCabecalho}>Celular</th>
                                            <th style={celaCabecalho}>Situação</th>
                                            <th style={celaCabecalho}>Motivo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {envio.naoRecebidos.map((linha) => {
                                            const classe = CLASSES[linha.CLASSE] || CLASSES.pendente;
                                            return (
                                                <tr key={linha.ID} style={{ borderTop: '1px solid #f1f5f9' }}>
                                                    <td style={cela}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selecionados.includes(linha.ID)}
                                                            onChange={() => alternarSelecao(linha.ID)}
                                                            aria-label={`Selecionar ${linha.NOME}`}
                                                        />
                                                    </td>
                                                    <td style={{ ...cela, fontWeight: 600, color: '#0f172a' }}>{linha.NOME || '—'}</td>
                                                    <td style={cela}>{linha.NOME_CENTRO_CUSTO || '—'}</td>
                                                    <td style={{ ...cela, fontVariantNumeric: 'tabular-nums' }}>{linha.CELULAR || '—'}</td>
                                                    <td style={cela}>
                                                        <span style={{
                                                            backgroundColor: classe.fundo, color: classe.cor, borderRadius: '99px',
                                                            padding: '3px 10px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap',
                                                        }}>
                                                            {classe.rotulo}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...cela, color: '#64748b', maxWidth: '320px' }}>
                                                        {linha.ULTIMO_ERRO || '—'}
                                                        {linha.ULTIMA_TENTATIVA && (
                                                            <span style={{ display: 'block', fontSize: '11.5px', color: '#94a3b8', marginTop: '2px' }}>
                                                                Última tentativa: {linha.ULTIMA_TENTATIVA}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '10px', lineHeight: 1.6 }}>
                                O reenvio busca o telefone atualizado no cadastro antes de tentar de novo —
                                corrigir o número no sistema de origem e reenviar resolve os casos de “Não enviado”.
                            </p>
                        </>
                    ) : (
                        <p style={{ fontSize: '13.5px', color: '#059669', margin: 0 }}>
                            <CheckCircle2 size={15} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                            Todo mundo da fila já recebeu o link.
                        </p>
                    )}

                    {envio.reenvios > 0 && (
                        <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '10px' }}>
                            {envio.reenvios} mensagem(ns) enviada(s) além da primeira de cada pessoa.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

// --- estilos compartilhados -------------------------------------------------

const celaCabecalho = { padding: '10px 12px', fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.02em', whiteSpace: 'nowrap' };
const cela = { padding: '10px 12px', verticalAlign: 'top' };

const botao = (tipo, desabilitado) => {
    const base = {
        display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px',
        borderRadius: '8px', fontWeight: 700, fontSize: '13px',
        cursor: desabilitado ? 'not-allowed' : 'pointer', opacity: desabilitado ? 0.6 : 1,
        transition: 'opacity 0.2s',
    };
    const variantes = {
        primario:   { backgroundColor: COR, color: '#fff', border: 'none' },
        secundario: { backgroundColor: COR_FUNDO, color: COR_ESCURA, border: `1px solid ${COR}` },
        neutro:     { backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' },
        perigo:     { backgroundColor: '#fff', color: '#b91c1c', border: '1px solid #fecaca' },
    };
    return { ...base, ...variantes[tipo] };
};

export default PesquisaEscalaConsulta;
